/**
 * Settings → Login activity: the server-side auth audit trail.
 *
 * Compact zebra list (hairline separators, count in the header, internal
 * scroll past ~8 rows) of login/password/setup events with caller IP and a
 * parsed device summary. While the viewing IP is serving a 24h ban, a red
 * banner counts it down live and re-pulls the log when it lapses.
 */
import { useCallback, useEffect, useState } from 'react';
import { RotateCcw, ShieldCheck } from 'lucide-react';
import { ApiError } from '../lib/api/client.js';
import { authApi, type LoginLogEvent, type LoginLogStatus } from '../lib/api/auth.js';

const pad = (value: number): string => String(value).padStart(2, '0');

/** Local "MM/DD HH:mm:ss" — column-stable thanks to tabular-nums. */
function formatTime(ts: number): string {
  const date = new Date(ts);
  return (
    `${pad(date.getMonth() + 1)}/${pad(date.getDate())}` +
    ` ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/** "23h 12m" / "4m 05s" / "42s" — the 24h ban must not read "86399s". */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  if (total >= 3600) {
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return `${hours}h ${pad(minutes)}m`;
  }
  if (total >= 60) return `${Math.floor(total / 60)}m ${pad(total % 60)}s`;
  return `${total}s`;
}

/** Machine reasons → short human labels for the trailing column. */
function reasonLabel(event: LoginLogEvent): string {
  if (event.outcome === 'blocked') return 'banned';
  if (event.action === 'password') {
    return event.outcome === 'success' ? 'password changed' : 'wrong password';
  }
  if (event.action === 'setup') return 'setup';
  switch (event.reason) {
    case 'invalid_password':
      return 'wrong password';
    case 'invalid_captcha':
      return 'captcha failed';
    case 'rate_limited':
      return 'rate limited';
    default:
      return event.reason;
  }
}

export function LoginActivitySection() {
  const [status, setStatus] = useState<LoginLogStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Ticks once per second while a ban banner is visible. */
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      setStatus(await authApi.loginLog());
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const banned = status?.blockedUntil != null && status.blockedUntil > now;

  useEffect(() => {
    if (!banned) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [banned]);

  // The ban lapsed while the dialog was open — pull once more so the banner
  // resolves itself instead of freezing at "0s".
  useEffect(() => {
    if (status?.blockedUntil != null && !banned) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banned]);

  const events = status?.events ?? [];

  return (
    <div className="modal-body settings-section">
      <div className="settings-section-title">
        <ShieldCheck size={13} strokeWidth={1.75} />
        <span>Login activity</span>
        <span className="loginlog-count">{events.length}</span>
        <button
          type="button"
          className="icon-button"
          onClick={() => void load()}
          disabled={busy}
          aria-label="Refresh login activity"
          title="Refresh"
        >
          <RotateCcw size={13} strokeWidth={1.75} />
        </button>
      </div>

      <p className="modal-text">
        {status
          ? `${status.policy.maxFailures} failed attempts within ` +
            `${status.policy.windowMinutes} minutes ban an IP for ` +
            `${status.policy.blockHours} hours.`
          : 'Successful and failed sign-ins, with visitor IP and device.'}
      </p>

      {banned && status?.blockedUntil != null && (
        <div className="loginlog-banned" role="alert">
          Your IP is banned — sign-in is unavailable for another{' '}
          {formatRemaining(status.blockedUntil - now)}.
        </div>
      )}

      {error && (
        <p className="settings-status settings-status-error" role="alert">
          {error}
        </p>
      )}

      {status && events.length === 0 ? (
        <div className="loginlog-empty">No login activity yet.</div>
      ) : (
        <div className="loginlog-list">
          <div className="loginlog-rows">
            {events.map((event) => (
              <div
                className="loginlog-row"
                key={`${event.ts}-${event.ip}-${event.action}`}
                title={
                  `${new Date(event.ts).toLocaleString()} · ${event.ip}` +
                  (event.failures != null ? ` · failure ${event.failures}` : '') +
                  ` · ${event.userAgent || 'no user agent'}`
                }
              >
                <span className={`loginlog-dot ${event.outcome}`} />
                <span className="loginlog-time">{formatTime(event.ts)}</span>
                <span className="loginlog-ip">{event.ip}</span>
                <span className="loginlog-device">{event.device}</span>
                <span className={`loginlog-reason ${event.outcome}`}>
                  {reasonLabel(event)}
                  {event.outcome === 'failure' &&
                  event.failures != null &&
                  event.failures > 1
                    ? ` · ${event.failures}`
                    : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
